// Persistent meta store — crystals, special points, cosmetic inventory,
// and lifetime stats. Everything lives in a single localStorage key
// (`smp.meta.v1`) as a JSON blob. Synchronous API, no backend.
//
// Awards flow: matches end → main.ts calls recordMatch(result) →
// wallet computes + applies rewards → returns { crystalsEarned,
// specialEarned } so the caller can show a popup.

import type { CosmeticSlot } from './cosmetics';
import { getCosmetic } from './cosmetics';

const STORAGE_KEY = 'smp.meta.v1';

export type EquippedSlots = {
  hat: string | null;
  skin: string | null;
  mimic: string | null;
  pet: string | null;
  trail: string | null;
};

export type MetaState = {
  version: 1;
  crystals: number;
  specialPoints: number;
  bestLevel: number;
  matchesPlayed: number;
  matchesWon: number;
  matchesTied: number;
  totalKills: number;
  crystalsEarned: number;
  ownedCosmetics: string[];
  pullsTotal: number;
  equipped: EquippedSlots;
};

export type MatchResult = {
  won: boolean;
  tie: boolean;
  level: number;
  kills: number;
};

export type MatchRewards = {
  crystalsEarned: number;
  specialEarned: number;
  levelBonus: number;
  killBonus: number;
};

const REWARDS = {
  PARTICIPATION_CRYSTALS: 50,
  WIN_CRYSTALS: 150,
  TIE_CRYSTALS: 75,
  PER_KILL_CRYSTALS: 10,
  PER_LEVEL_CRYSTALS: 15,

  WIN_SPECIAL: 10,
  TIE_SPECIAL: 5,
} as const;

function freshEquipped(): EquippedSlots {
  return { hat: null, skin: null, mimic: null, pet: null, trail: null };
}

function freshState(): MetaState {
  return {
    version: 1,
    crystals: 0,
    specialPoints: 0,
    bestLevel: 1,
    matchesPlayed: 0,
    matchesWon: 0,
    matchesTied: 0,
    totalKills: 0,
    crystalsEarned: 0,
    ownedCosmetics: [],
    pullsTotal: 0,
    equipped: freshEquipped(),
  };
}

let cache: MetaState | null = null;
const listeners = new Set<() => void>();

function load(): MetaState {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<MetaState> & { version?: number };
      if (parsed.version === 1) {
        // Backfill `equipped` for blobs that predate the cosmetics update.
        const merged: MetaState = { ...freshState(), ...parsed } as MetaState;
        merged.equipped = { ...freshEquipped(), ...(parsed.equipped ?? {}) };
        cache = merged;
        return cache;
      }
    }
  } catch { /* ignore — fall back to fresh */ }
  cache = freshState();
  return cache;
}

function save(): void {
  if (!cache) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch { /* quota or disabled — tolerate */ }
  for (const l of listeners) l();
}

export function onChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

export function getState(): Readonly<MetaState> {
  return load();
}

export function getCrystals(): number {
  return load().crystals;
}

export function getSpecialPoints(): number {
  return load().specialPoints;
}

export function getWinRate(): number {
  const s = load();
  return s.matchesPlayed > 0 ? s.matchesWon / s.matchesPlayed : 0;
}

export function ownsItem(id: string): boolean {
  return load().ownedCosmetics.includes(id);
}

export function addCrystals(n: number): void {
  const s = load();
  s.crystals = Math.max(0, s.crystals + n);
  if (n > 0) s.crystalsEarned += n;
  save();
}

export function addSpecialPoints(n: number): void {
  const s = load();
  s.specialPoints = Math.max(0, s.specialPoints + n);
  save();
}

export function spendCrystals(n: number): boolean {
  const s = load();
  if (s.crystals < n) return false;
  s.crystals -= n;
  save();
  return true;
}

export function spendSpecial(n: number): boolean {
  const s = load();
  if (s.specialPoints < n) return false;
  s.specialPoints -= n;
  save();
  return true;
}

export function grantItem(id: string): void {
  const s = load();
  if (!s.ownedCosmetics.includes(id)) {
    s.ownedCosmetics.push(id);
    save();
  }
}

export function getEquipped(slot: CosmeticSlot): string | null {
  return load().equipped[slot];
}

export function getAllEquipped(): Readonly<EquippedSlots> {
  return load().equipped;
}

export function equip(id: string): boolean {
  const def = getCosmetic(id);
  if (!def) return false;
  const s = load();
  if (!s.ownedCosmetics.includes(id)) return false;
  s.equipped[def.slot] = id;
  save();
  return true;
}

export function unequip(slot: CosmeticSlot): void {
  const s = load();
  s.equipped[slot] = null;
  save();
}

export function isEquipped(id: string): boolean {
  const def = getCosmetic(id);
  if (!def) return false;
  return load().equipped[def.slot] === id;
}

export function registerPull(): void {
  const s = load();
  s.pullsTotal += 1;
  save();
}

export function recordMatch(result: MatchResult): MatchRewards {
  const s = load();
  s.matchesPlayed += 1;
  if (result.won) s.matchesWon += 1;
  else if (result.tie) s.matchesTied += 1;
  if (result.level > s.bestLevel) s.bestLevel = result.level;
  s.totalKills += result.kills;

  const killBonus = REWARDS.PER_KILL_CRYSTALS * result.kills;
  const levelBonus = REWARDS.PER_LEVEL_CRYSTALS * result.level;
  let crystalsEarned = REWARDS.PARTICIPATION_CRYSTALS + killBonus + levelBonus;
  let specialEarned = 0;
  if (result.won) {
    crystalsEarned += REWARDS.WIN_CRYSTALS;
    specialEarned += REWARDS.WIN_SPECIAL;
  } else if (result.tie) {
    crystalsEarned += REWARDS.TIE_CRYSTALS;
    specialEarned += REWARDS.TIE_SPECIAL;
  }
  s.crystals += crystalsEarned;
  s.specialPoints += specialEarned;
  s.crystalsEarned += crystalsEarned;
  save();
  return { crystalsEarned, specialEarned, levelBonus, killBonus };
}

// Debug / test helper — fully wipes the meta state. Not bound to UI.
export function resetAll(): void {
  cache = freshState();
  save();
}
